'use client'

import { Button, Dialog, Heading, Input, InputHelperText, InputLabel } from '@graftcode/design-system'

import type { GatewayConfig } from '@/core/api'
import type { GatewayUserSettings } from '@/core/utils/interfaces/gatewayUserSettings.interface'

import { useGatewaySettingsDialog } from './hooks/useGatewaySettingsDialog'

import styles from './GatewaySettingsDialog.module.scss'

interface GatewaySettingsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  gatewaySettings: GatewayUserSettings
  baseConfig: GatewayConfig
  applyGatewaySettings: (settings: GatewayUserSettings) => void
  resetGatewaySettings: () => void
  onRefetchGateway: () => Promise<unknown>
}

export function GatewaySettingsDialog({
  open,
  onOpenChange,
  gatewaySettings,
  baseConfig,
  applyGatewaySettings,
  resetGatewaySettings,
  onRefetchGateway,
}: GatewaySettingsDialogProps) {
  const dialog = useGatewaySettingsDialog({
    open,
    gatewaySettings,
    baseConfig,
    applyGatewaySettings,
    resetGatewaySettings,
    onRefetchGateway,
    onOpenChange,
  })

  return (
    <Dialog
      open={open}
      noPadding
      showDismiss
      onOpenChange={onOpenChange}
      onDismiss={dialog.handleDismiss}
      maxWidth="35.5rem"
      className={styles.dialog}
      ariaLabelledBy="gateway-settings-title"
      ariaDescribedBy="gateway-settings-description"
    >
      <div className={styles.root}>
        <div className={styles.header}>
          <Heading type="h4" id="gateway-settings-title">
            Gateway connection
          </Heading>
          <p id="gateway-settings-description" className={styles.description}>
            Override host and ports when auto-detect from the browser does not match your local gateway.
          </p>
        </div>

        <div className={styles.content}>
          <div className={styles.fieldBlock}>
            <InputLabel htmlFor="gateway-host">Host</InputLabel>
            <Input
              id="gateway-host"
              name="gateway-host"
              size="md"
              value={dialog.formValues.host ?? ''}
              onChange={(event) => dialog.updateField('host', event.target.value)}
            />
            {dialog.errors.host ? <InputHelperText variant="error">{dialog.errors.host}</InputHelperText> : null}
          </div>

          <div className={styles.portsGrid}>
            <div className={styles.fieldBlock}>
              <InputLabel htmlFor="gateway-http-port">HTTP port</InputLabel>
              <Input
                id="gateway-http-port"
                name="gateway-http-port"
                size="md"
                inputMode="numeric"
                value={dialog.formValues.httpPort ?? ''}
                onChange={(event) => dialog.updateField('httpPort', event.target.value)}
              />
              {dialog.errors.httpPort ? (
                <InputHelperText variant="error">{dialog.errors.httpPort}</InputHelperText>
              ) : null}
            </div>

            <div className={styles.fieldBlock}>
              <InputLabel htmlFor="gateway-ws-port">WebSocket port</InputLabel>
              <Input
                id="gateway-ws-port"
                name="gateway-ws-port"
                size="md"
                inputMode="numeric"
                value={dialog.formValues.wsPort ?? ''}
                onChange={(event) => dialog.updateField('wsPort', event.target.value)}
                placeholder="Same as HTTP"
              />
              {dialog.errors.wsPort ? <InputHelperText variant="error">{dialog.errors.wsPort}</InputHelperText> : null}
            </div>

            <div className={styles.fieldBlock}>
              <InputLabel htmlFor="gateway-mcp-port">MCP port</InputLabel>
              <Input
                id="gateway-mcp-port"
                name="gateway-mcp-port"
                size="md"
                inputMode="numeric"
                value={dialog.formValues.mcpPort ?? ''}
                onChange={(event) => dialog.updateField('mcpPort', event.target.value)}
                placeholder="Same as HTTP"
              />
              {dialog.errors.mcpPort ? (
                <InputHelperText variant="error">{dialog.errors.mcpPort}</InputHelperText>
              ) : null}
            </div>
          </div>
        </div>

        <div className={styles.footer}>
          <div className={styles.footerReset}>
            <Button
              text="Reset to auto-detect"
              variant="ghost"
              size="m"
              className={styles.resetButton}
              onClick={dialog.handleReset}
            />
          </div>
          <div className={styles.footerActions}>
            <Button text="Cancel" variant="outlined" size="m" onClick={dialog.handleDismiss} />
            <Button text="Save" size="m" onClick={dialog.handleSave} />
          </div>
        </div>
      </div>
    </Dialog>
  )
}
